import { 
  X, 
  AlertTriangle, 
  Calendar, 
  MapPin, 
  Clock, 
  DollarSign, 
  TrendingDown,
  CheckCircle,
  Plus,
  Settings
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert } from '@/types'
import { formatDateTime, formatCurrency } from '@/lib/utils'

interface AlertDetailModalProps {
  alert: Alert
  isOpen: boolean
  onClose: () => void
  onAcknowledge: (alertId: string) => void
  onCreateWorkOrder: (alert: Alert) => void
}

export default function AlertDetailModal({
  alert,
  isOpen,
  onClose,
  onAcknowledge,
  onCreateWorkOrder
}: AlertDetailModalProps) {
  if (!isOpen) return null

  const getPriorityVariant = (priority: Alert['priority']): 'critical' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case 'critical': return 'critical'
      case 'high': return 'critical'
      case 'warning': return 'warning'
      case 'info': return 'info'
      default: return 'default'
    }
  }

  const getStatusVariant = (status: Alert['status']): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'resolved': return 'success'
      case 'acknowledged': return 'warning'
      case 'work-order-created': return 'info'
      case 'new': return 'default'
      default: return 'default'
    }
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-success-green'
    if (confidence >= 60) return 'text-warning-amber'
    return 'text-critical-red'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-6 h-6 ${
              alert.priority === 'critical' ? 'text-critical-red' :
              alert.priority === 'high' ? 'text-orange-500' :
              alert.priority === 'warning' ? 'text-warning-amber' :
              'text-info-blue'
            }`} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{alert.title}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={getPriorityVariant(alert.priority)}>
                  {(alert.priority || 'unknown').charAt(0).toUpperCase() + (alert.priority || 'unknown').slice(1)} Priority
                </Badge>
                <Badge variant={getStatusVariant(alert.status)}>
                  {alert.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Alert Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
            <p className="text-gray-600">{alert.description}</p>
          </div>

          {/* Equipment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Equipment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Equipment Name</p>
                  <p className="text-gray-900">{alert.equipment?.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Type</p>
                  <p className="text-gray-900 capitalize">{alert.equipment?.type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Location</p>
                  <div className="flex items-center gap-1 text-gray-900">
                    <MapPin className="w-3 h-3" />
                    <span>
                      {alert.equipment?.location?.building} 
                      {alert.equipment?.location?.floor && ` - Floor ${alert.equipment?.location?.floor}`}
                      {alert.equipment?.location?.room && ` - ${alert.equipment?.location?.room}`}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Health Score</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          (alert.equipment?.healthScore || 0) >= 80 ? 'bg-success-green' :
                          (alert.equipment?.healthScore || 0) >= 60 ? 'bg-warning-amber' :
                          'bg-critical-red'
                        }`}
                        style={{ width: `${alert.equipment?.healthScore || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {alert.equipment?.healthScore || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prediction Information */}
          {alert.prediction && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  Predictive Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Time to Failure</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-900 font-medium">{alert.prediction.timeToFailure}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Confidence</p>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${getConfidenceColor(alert.prediction.confidence)}`}>
                        {alert.prediction.confidence}%
                      </span>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            alert.prediction.confidence >= 80 ? 'bg-success-green' :
                            alert.prediction.confidence >= 60 ? 'bg-warning-amber' :
                            'bg-critical-red'
                          }`}
                          style={{ width: `${alert.prediction.confidence}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Failure Mode</p>
                    <p className="text-gray-900">{alert.prediction.failureMode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Estimated Cost</p>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-900 font-medium">
                        {formatCurrency(alert.prediction.estimatedCost)}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({formatCurrency(alert.prediction.costRange.min)} - {formatCurrency(alert.prediction.costRange.max)})
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Actions */}
          {alert.recommendedActions && alert.recommendedActions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recommended Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {alert.recommendedActions.map((action, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-trust-blue rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{action}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-info-blue rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Alert Created</p>
                    <p className="text-xs text-gray-500">{formatDateTime(alert.createdAt)}</p>
                  </div>
                </div>
                {alert.acknowledgedAt && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-warning-amber rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Acknowledged</p>
                      <p className="text-xs text-gray-500">{formatDateTime(alert.acknowledgedAt)}</p>
                      {alert.acknowledgedBy && (
                        <p className="text-xs text-gray-500">by {alert.acknowledgedBy}</p>
                      )}
                    </div>
                  </div>
                )}
                {alert.workOrderId && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-info-blue rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Work Order Created</p>
                      <p className="text-xs text-gray-500">ID: {alert.workOrderId}</p>
                    </div>
                  </div>
                )}
                {alert.resolvedAt && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-success-green rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Resolved</p>
                      <p className="text-xs text-gray-500">{formatDateTime(alert.resolvedAt)}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          
          {alert.status === 'new' && (
            <Button
              variant="outline"
              onClick={() => {
                onAcknowledge(alert.id)
                onClose()
              }}
              className="flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Acknowledge
            </Button>
          )}
          
          {(alert.status === 'new' || alert.status === 'acknowledged') && (
            <Button
              onClick={() => {
                onCreateWorkOrder(alert)
                onClose()
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Work Order
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}